import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Calendar,
  ChevronDown,
  ExternalLink,
  MapPin,
  Briefcase,
  Clock,
  Building2
} from 'lucide-react';
import { requirementApi } from '../feddback-services/api';
import { useNavigate } from 'react-router-dom';

const NoticeBoard = () => {
  const navigate = useNavigate();

  const [expandedId, setExpandedId] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ============= FETCH REQUIREMENTS =============
  const fetchRequirements = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await requirementApi.list({ status: 'open' });
      const data = response.data.data || response.data;

      setRequirements(data.items || []);
    } catch (err) {
      console.error('Error fetching requirements:', err);
      setError('Failed to load notices. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ============= INITIAL LOAD =============
  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  // ============= HANDLERS =============
  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleApplyNow = () => {
    navigate('/login');
  };

  // ============= LOCATION HELPER =============
  const getLocationStrings = (req) => {
    if (!req) return [];

    const locationSources = [
      req.locations,
      req.location ? [req.location] : [],
      req.locations_data,
      req.locations_list
    ];

    for (const source of locationSources) {
      if (!source) continue;

      if (Array.isArray(source)) {
        const locations = source
          .map((loc) => {
            if (typeof loc === 'string') {
              return loc.trim();
            }

            if (typeof loc === 'object' && loc !== null) {
              return (
                loc.name ||
                loc.location ||
                loc.city ||
                loc.address ||
                ''
              );
            }

            return '';
          })
          .filter(Boolean);

        if (locations.length > 0) {
          return locations;
        }
      }
    }

    if (req.location && typeof req.location === 'string') {
      if (req.location.includes(',')) {
        return req.location
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }

      return [req.location];
    }

    return [];
  };

  // ============= DATE FORMAT =============
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateFull = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ============= SELECTED ITEM =============
  const selectedItem = requirements.find(
    (req) => req.id === expandedId
  );

  return (
    <div className="notice-board-horizontal">
      <style>{`
        /* ============================================
           HORIZONTAL NOTICE BOARD
           ============================================ */

        .notice-board-horizontal {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        /* ============================================
           HEADER
           ============================================ */

        .notice-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 7px 12px;
          background: #f0f4f8;
          border-radius: 8px 8px 0 0;
          border-right: 4px solid #008986;
          border-bottom: 2px solid #e0e8e6;
          min-height: 52px;
          overflow: hidden;
        }

        /* ============================================
           AVAILABLE POSITIONS LABEL
           ============================================ */

        .notice-header-label {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 14px;
          font-weight: 700;
          color: #00524f;
          white-space: nowrap;
          flex-shrink: 0;
          padding-left: 4px;
        }

        .notice-header-label svg {
          color: #008986;
          stroke-width: 2;
        }

        .notice-header-label .count-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 25px;
          height: 25px;
          padding: 0 7px;
          background: #008986;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          border-radius: 50%;
          line-height: 1;
        }

        /* ============================================
           NOTICE TICKER
           ============================================ */

        .notice-tabs {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          position: relative;
          padding: 2px 0;
        }

        /*
          Track contains two copies of the notices.
          This creates a seamless right-to-left loop.
        */

        .notice-tabs-track {
          display: flex;
          align-items: center;
          gap: 8px;
          width: max-content;
          animation: noticeTicker 15s linear infinite;
          will-change: transform;
        }

        /*
          Pause when mouse is over notices.
        */

        .notice-tabs:hover .notice-tabs-track {
          animation-play-state: paused;
        }

        /*
          Right -> Left
        */

        @keyframes noticeTicker {
          from {
            transform: translateX(0);
          }

          to {
            transform: translateX(-50%);
          }
        }

        /* ============================================
           NOTICE TAB
           ============================================ */

.notice-tab {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border: 1px solid #0caea9;
  border-radius: 5px;
  background: #0a6f6c;
  font-size: 12px;
  font-weight: 500;
  color: #ffffff;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.notice-tab:hover {
  background: #0da7a2;
  border-color: #10b3ae;
  color: #ffffff;
}

.notice-tab.active {
  background: #02918c;
  border-color: #099f9a;
  color: #ffffff;
}

.notice-tab .tab-date {
  font-size: 10px;
  color: #b8d7d5;
  font-weight: 400;
}

.notice-tab.active .tab-date {
  color: #d8eeee;
}

.notice-tab .tab-toggle {
  color: #ffffff;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.notice-tab.active .tab-toggle {
  transform: rotate(180deg);
}

        /* ============================================
           DETAILS PANEL
           ============================================ */

        .notice-details {
          padding: 16px 24px 20px;
          background: #ffffff;
          border: 1px solid #e0e8e6;
          border-top: none;
          border-radius: 0 0 8px 8px;
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* ============================================
           DETAIL HEADER
           ============================================ */

        .notice-detail-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 2px solid #eaf3f2;
        }

        .notice-detail-title {
          font-size: 18px;
          font-weight: 700;
          color: #00524f;
          font-family: 'Playfair Display', serif;
          margin: 0 0 5px;
        }

        .notice-detail-code {
          display: inline-block;
          font-size: 12px;
          color: #9aa8a6;
          font-weight: 500;
          background: #f0f4f8;
          padding: 2px 12px;
          border-radius: 4px;
          white-space: nowrap;
        }

        .notice-close-btn {
          background: none;
          border: none;
          color: #9aa8a6;
          cursor: pointer;
          font-size: 13px;
          padding: 4px 8px;
          transition: color 0.2s;
          font-family: 'Inter', sans-serif;
        }

        .notice-close-btn:hover {
          color: #1a2422;
        }

        /* ============================================
           META
           ============================================ */

        .notice-detail-meta {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 8px 24px;
          padding: 12px 16px;
          background: #f7faf9;
          border-radius: 6px;
          margin-bottom: 16px;
        }

        .notice-detail-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #1a2422;
        }

        .notice-detail-item .icon {
          color: #008986;
          flex-shrink: 0;
        }

        .notice-detail-item strong {
          color: #5c6c6a;
          font-weight: 600;
          font-size: 12px;
          white-space: nowrap;
        }

        .notice-detail-item .value {
          color: #1a2422;
        }

        .notice-detail-locations {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }

        .notice-location-tag {
          background: #eaf3f2;
          color: #008986;
          padding: 1px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
        }

        /* ============================================
           APPLY BUTTON
           ============================================ */

        .notice-apply-btn {
          background: #008986;
          color: #fff;
          border: none;
          padding: 10px 28px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s ease;
          font-family: 'Inter', sans-serif;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          justify-content: center;
        }

        .notice-apply-btn:hover {
          background: #00524f;
        }

        .notice-apply-btn .icon {
          font-size: 16px;
        }

        /* ============================================
           LOADING
           ============================================ */

        .notice-loading {
          text-align: center;
          padding: 30px 20px;
          background: #fff;
          border: 1px solid #e0e8e6;
          border-radius: 0 0 8px 8px;
        }

        .notice-spinner {
          width: 28px;
          height: 28px;
          margin: 0 auto 10px;
          border: 3px solid #eaf3f2;
          border-top: 3px solid #008986;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* ============================================
           EMPTY / ERROR
           ============================================ */

        .notice-empty {
          text-align: center;
          padding: 30px 20px;
          background: #fff;
          border: 1px solid #e0e8e6;
          border-radius: 0 0 8px 8px;
          color: #5c6c6a;
          font-size: 14px;
        }

        /* ============================================
           RESPONSIVE - TABLET
           ============================================ */

        @media (max-width: 768px) {
          .notice-header {
            padding: 6px 10px;
            min-height: 46px;
          }

          .notice-header-label {
            font-size: 12px;
            gap: 5px;
          }

          .notice-header-label .count-badge {
            min-width: 22px;
            height: 22px;
            font-size: 10px;
          }

          .notice-tab {
            font-size: 11px;
            padding: 5px 10px;
          }

          .notice-tab .tab-date {
            font-size: 9px;
          }

          .notice-details {
            padding: 14px 14px 16px;
          }

          .notice-detail-title {
            font-size: 16px;
          }

          .notice-detail-meta {
            grid-template-columns: 1fr 1fr;
            gap: 6px 16px;
          }

          .notice-detail-item {
            font-size: 12px;
          }
        }

        /* ============================================
           RESPONSIVE - MOBILE
           ============================================ */

        @media (max-width: 480px) {
          .notice-header {
            gap: 7px;
            padding: 5px 8px;
            min-height: 42px;
          }

          .notice-header-label {
            font-size: 11px;
          }

          .notice-header-label svg {
            width: 13px;
            height: 13px;
          }

          .notice-header-label .count-badge {
            min-width: 20px;
            height: 20px;
            padding: 0 6px;
            font-size: 9px;
          }

          .notice-tab {
            font-size: 10px;
            padding: 4px 8px;
          }

          .notice-tab .tab-date {
            font-size: 8px;
          }

          .notice-details {
            padding: 10px 10px 14px;
          }

          .notice-detail-title {
            font-size: 14px;
          }

          .notice-detail-meta {
            grid-template-columns: 1fr;
            gap: 5px;
          }

          .notice-detail-item {
            font-size: 11px;
          }

          .notice-detail-item strong {
            font-size: 11px;
          }

          .notice-apply-btn {
            font-size: 13px;
            padding: 8px 20px;
          }
        }

        /* ============================================
           REDUCED MOTION
           ============================================ */

        @media (prefers-reduced-motion: reduce) {
          .notice-tabs-track {
            animation: none;
          }
        }
      `}</style>

      {/* ============================================
          HEADER
          ============================================ */}

      <div className="notice-header">

        {/* AVAILABLE POSITIONS */}

        <span className="notice-header-label">
          <FileText size={14} />

          Available Positions

          <span className="count-badge">
            {requirements.length}
          </span>
        </span>

        {/* ============================================
            NOTICE TICKER
            ============================================ */}

        {!loading &&
          !error &&
          requirements.length > 0 && (
            <div className="notice-tabs">

              <div className="notice-tabs-track">

                {/* FIRST COPY */}

                {requirements.map((req, index) => (
                  <button
                    key={`first-${req.id}`}
                    className={`notice-tab ${
                      expandedId === req.id ? 'active' : ''
                    }`}
                    onClick={() => toggleExpand(req.id)}
                  >
                    {index + 1}. {req.title}

                    <span className="tab-date">
                      {formatDate(req.created_at)}
                    </span>

                    <ChevronDown
                      size={12}
                      className="tab-toggle"
                    />
                  </button>
                ))}

                {/* SECOND COPY
                    Used for seamless infinite scrolling
                */}

                {requirements.map((req, index) => (
                  <button
                    key={`second-${req.id}`}
                    className={`notice-tab ${
                      expandedId === req.id ? 'active' : ''
                    }`}
                    onClick={() => toggleExpand(req.id)}
                  >
                    {index + 1}. {req.title}

                    <span className="tab-date">
                      {formatDate(req.created_at)}
                    </span>

                    <ChevronDown
                      size={12}
                      className="tab-toggle"
                    />
                  </button>
                ))}

              </div>
            </div>
          )}

        {/* LOADING TEXT */}

        {loading && (
          <span
            style={{
              fontSize: '12px',
              color: '#9aa8a6',
              padding: '4px 8px'
            }}
          >
            Loading...
          </span>
        )}

      </div>

      {/* ============================================
          LOADING
          ============================================ */}

      {loading && (
        <div className="notice-loading">

          <div className="notice-spinner"></div>

          <p
            style={{
              color: '#5c6c6a',
              fontSize: '13px'
            }}
          >
            Loading positions...
          </p>

        </div>
      )}

      {/* ============================================
          ERROR
          ============================================ */}

      {error && !loading && (
        <div className="notice-empty">

          <span
            style={{
              fontSize: '24px',
              display: 'block',
              marginBottom: '6px'
            }}
          >
            ⚠️
          </span>

          <p>{error}</p>

        </div>
      )}

      {/* ============================================
          EMPTY
          ============================================ */}

      {!loading &&
        !error &&
        requirements.length === 0 && (
          <div className="notice-empty">

            <span
              style={{
                fontSize: '24px',
                display: 'block',
                marginBottom: '6px'
              }}
            >
              📋
            </span>

            <p>
              No positions available at the moment.
            </p>

          </div>
        )}

      {/* ============================================
          EXPANDED DETAILS
          ============================================ */}

      {!loading &&
        !error &&
        selectedItem && (
          <div className="notice-details">

            {/* DETAIL HEADER */}

            <div className="notice-detail-header">

              <div>

                <h3 className="notice-detail-title">
                  {selectedItem.title}
                </h3>

                <span className="notice-detail-code">
                  {selectedItem.requirement_code}
                </span>

              </div>

              <button
                className="notice-close-btn"
                onClick={() => setExpandedId(null)}
              >
                ✕ Close
              </button>

            </div>

            {/* META INFORMATION */}

            <div className="notice-detail-meta">

              {/* DEPARTMENT */}

              <div className="notice-detail-item">

                <Building2
                  size={14}
                  className="icon"
                />

                <strong>
                  Department:
                </strong>

                <span className="value">
                  {selectedItem.department || 'N/A'}
                </span>

              </div>

              {/* JOB TYPE */}

              <div className="notice-detail-item">

                <Briefcase
                  size={14}
                  className="icon"
                />

                <strong>
                  Job Type:
                </strong>

                <span
                  className="value"
                  style={{
                    textTransform: 'capitalize'
                  }}
                >
                  {selectedItem.job_type || 'N/A'}
                </span>

              </div>

              {/* EXPERIENCE */}

              <div className="notice-detail-item">

                <Clock
                  size={14}
                  className="icon"
                />

                <strong>
                  Experience:
                </strong>

                <span className="value">
                  {selectedItem.experience_min || 0}
                  {' - '}
                  {selectedItem.experience_max || 'Any'}
                  {' yrs'}
                </span>

              </div>

              {/* LOCATIONS */}

              <div
                className="notice-detail-item"
                style={{
                  gridColumn: '1 / -1'
                }}
              >

                <MapPin
                  size={14}
                  className="icon"
                />

                <strong>
                  Locations:
                </strong>

                <div className="notice-detail-locations">

                  {getLocationStrings(selectedItem).length > 0 ? (
                    getLocationStrings(selectedItem).map(
                      (loc, idx) => (
                        <span
                          key={idx}
                          className="notice-location-tag"
                        >
                          {loc}
                        </span>
                      )
                    )
                  ) : (
                    'N/A'
                  )}

                </div>

              </div>

              {/* POSTED ON */}

              <div
                className="notice-detail-item"
                style={{
                  gridColumn: '1 / -1'
                }}
              >

                <Calendar
                  size={14}
                  className="icon"
                />

                <strong>
                  Posted On:
                </strong>

                <span className="value">
                  {formatDateFull(
                    selectedItem.created_at
                  )}
                </span>

              </div>

            </div>

            {/* APPLY NOW */}

            <button
              className="notice-apply-btn"
              onClick={handleApplyNow}
            >

              <ExternalLink
                size={16}
                className="icon"
              />

              Apply Now

            </button>

          </div>
        )}

    </div>
  );
};

export default NoticeBoard;